import type { Category, CategoryGroup } from '@fd/schemas';
import { orderCategories } from '../app/category-order.ts';
import { useT } from '../app/i18n.tsx';
import { categoryLabel } from './TransactionRow.tsx';

/** A group's name as the owner wrote it. */
export const groupLabel = (group: Pick<CategoryGroup, 'name' | 'key'>) => group.name ?? group.key ?? '—';

/** The value that stands for a whole group in a category select or the transactions filter. */
export const wholeGroup = (groupId: string) => `group:${groupId}`;

/**
 * Options for a category select (D-035, D-036): in the owner's order from Budgets, a main category
 * with subcategories as its own section — with "the whole group" first when `wholeGroups` is on.
 */
export function CategoryOptions({ categories, groups, wholeGroups = false }: { categories: readonly Category[]; groups: readonly CategoryGroup[]; wholeGroups?: boolean }) {
  const t = useT();
  const mains = orderCategories(categories, groups, (x) => ('kind' in x ? categoryLabel(t, x) : groupLabel(x)), t.locale);
  return (
    <>
      {mains.map((main) => {
        if (!main.group)
          return (
            <option key={main.key} value={main.category.id}>
              {categoryLabel(t, main.category)}
            </option>
          );
        const group = main.group;
        if (main.members.length === 0) return null;
        return (
          <optgroup key={main.key} label={groupLabel(group)}>
            {wholeGroups && <option value={wholeGroup(group.id)}>{t.t('transactions.wholeGroup', { name: groupLabel(group) })}</option>}
            {main.members.map((c) => (
              <option key={c.id} value={c.id}>
                {categoryLabel(t, c)}
              </option>
            ))}
          </optgroup>
        );
      })}
    </>
  );
}
